import {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { getBitacorasPadrones } from 'api/tramites/bitacora';
import { getUsuarios } from 'api/usuarios/usuarios';
import { discartNullValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import {
  normalizeSearchParams,
  getPadronOptions,
  getAccionesByPadron,
  getModalTitle,
  extractClaveFromRecord,
  extractDescripcionFromRecord,
  formatFecha,
  GLOBAL_ACCIONES,
  ACCIONES_TRAMITES,
  ACCIONES_POR_PADRON,
} from 'utils/bitacoraHelpers';

// eslint-disable-next-line import/prefer-default-export
export const useBitacora = () => {
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [data, setData] = useState({ results: [] });
  const [selected, setSelected] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchParams, setSearchParams] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [usuariosLoading, setUsuariosLoading] = useState(false);

  const opcionesPadrones = useMemo(() => getPadronOptions(), []);

  const getNombreUsuario = useCallback((id, row) => {
    if (row?.accion_inf === 'LOGIN') {
      return row.data_nueva?.email || '';
    }

    if (!id || !usuarios.length) return '';

    const user = usuarios.find((u) => u.id === id);
    return user?.nombre_completo || '';
  }, [usuarios]);

  const fetchUsuarios = useCallback(async () => {
    setUsuariosLoading(true);
    try {
      const usuariosData = await getUsuarios();
      setUsuarios(usuariosData);
    } catch (error) {
      onError(error);
    } finally {
      setUsuariosLoading(false);
    }
  }, []);

  const fetchData = useCallback(async (page = 1, params = null) => {
    setLoading(true);
    setCurrentPage(page);

    try {
      const bitacoraData = await getBitacorasPadrones({
        page,
        page_size: 10,
        ...params,
        ...searchParams,
      });

      bitacoraData.params = params;
      setData(bitacoraData);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const wipe = useCallback(() => {
    setSearchParams({});
    setSelectedRowKeys([]);
    setSelected(null);
    setCurrentPage(1);
    setData({ results: [], count: 0, params: {} });

    return {
      id_padron: undefined,
      usuario_creo: undefined,
      clave: undefined,
      fecha_inicio: undefined,
      fecha_fin: undefined,
      accion_inf: undefined,
    };
  }, []);

  const handleOnRowClick = useCallback((record) => {
    setSelectedRowKeys([record.id]);
    setSelected(record);
  }, []);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    type: 'radio',
  }), [selectedRowKeys]);

  const handleSearch = useCallback(async (formValues) => {
    try {
      setLoading(true);
      const paramsSearch = normalizeSearchParams(formValues);
      const keys = Object.keys(discartNullValues(paramsSearch));
      if (!keys.length) {
        setData({ results: [] });
        throw new Error('Ingrese al menos un campo para realizar la búsqueda');
      }
      const bitacoraData = await getBitacorasPadrones({
        ...paramsSearch,
        page: 1,
        page_size: 10,
      });
      bitacoraData.params = paramsSearch;
      setData(bitacoraData);
      setSearchParams(paramsSearch);
      setCurrentPage(1);
      return bitacoraData;
    } catch (error) {
      onError(error, () => setLoading(false));
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getAccionesOptions = useCallback((padronId) => getAccionesByPadron(padronId), []);
  const getModalTitleFromSelected = useCallback(() => getModalTitle(selected), [selected]);
  const ALL_ACCIONES = useMemo(() => [
    ...GLOBAL_ACCIONES,
    ...ACCIONES_TRAMITES,
    ...Object.values(ACCIONES_POR_PADRON).flat(),
  ], []);
  const getColumns = useCallback(() => [
    {
      titleText: 'Clave / ID',
      dataIndex: 'clave',
      key: 'clave',
      width: 200,
      render: (text, row) => extractClaveFromRecord(row),
    },
    {
      titleText: 'Descripcion',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
      render: (text, row) => extractDescripcionFromRecord(row),
    },
    {
      titleText: 'Usuario creó',
      dataIndex: 'usuario_creo',
      key: 'usuario_creo',
      width: 200,
      render: (id, row) => getNombreUsuario(id, row),
    },
    {
      titleText: 'Fecha',
      dataIndex: 'ultima_fecha_de_modificacion',
      key: 'ultima_fecha_de_modificacion',
      width: 200,
      render: (text) => formatFecha(text),
    },
    {
      titleText: 'Acción realizada',
      dataIndex: 'accion_inf',
      key: 'accion_inf',
      width: 200,
      render: (text) => {
        if (!text) return '';
        const match = ALL_ACCIONES.find((a) => a.id === text);
        return match ? match.descripcion.toUpperCase() : text.replace(/_/g, ' ');
      },
    },
  ], [getNombreUsuario]);

  const handlePageChange = useCallback((page) => {
    fetchData(page, searchParams);
  }, [fetchData, searchParams]);

  return {
    loading: loading || usuariosLoading,
    usuarios,
    data,
    selected,
    selectedRowKeys,
    searchParams,
    currentPage,
    opcionesPadrones,
    getColumns,
    getAccionesOptions,
    getModalTitleFromSelected,
    fetchData,
    wipe,
    handleOnRowClick,
    rowSelection,
    handleSearch,
    handlePageChange,
    setSelected,
    setSelectedRowKeys,
    setSearchParams,
    bitacoraLoading: loading,
    usuariosLoading,
  };
};
